/**
 * Workshop-CRUD-API. Container für die fünf Strategy-Module.
 */
import { api } from "@/api/client";

export type WorkshopStatus = "draft" | "in_progress" | "completed" | "archived";

export interface Workshop {
  id: string;
  tenant_id: string;
  owner_user_id: string;
  customer_id: string | null;
  name: string;
  status: WorkshopStatus;
  locale: string;
  brand_brief_id: string | null;
  llm_config_id: string | null;
  created_at: string;
  updated_at: string;
}

export interface WorkshopCreate {
  name: string;
  locale?: string;
  brand_brief_id?: string | null;
  llm_config_id?: string | null;
  customer_id?: string | null;
}

export interface WorkshopUpdate {
  name?: string;
  status?: WorkshopStatus;
  locale?: string;
  brand_brief_id?: string | null;
  llm_config_id?: string | null;
  customer_id?: string | null;
}

export async function listWorkshops(params?: {
  customer_id?: string;
}): Promise<Workshop[]> {
  const { data } = await api.get<Workshop[]>("/v1/workshops", { params });
  return data;
}

export async function getWorkshop(id: string): Promise<Workshop> {
  const { data } = await api.get<Workshop>(`/v1/workshops/${id}`);
  return data;
}

export async function createWorkshop(payload: WorkshopCreate): Promise<Workshop> {
  const { data } = await api.post<Workshop>("/v1/workshops", payload);
  return data;
}

export async function updateWorkshop(
  id: string,
  payload: WorkshopUpdate,
): Promise<Workshop> {
  const { data } = await api.patch<Workshop>(`/v1/workshops/${id}`, payload);
  return data;
}

export async function deleteWorkshop(id: string): Promise<void> {
  await api.delete(`/v1/workshops/${id}`);
}

/**
 * Lädt den Workshop-PDF-Bericht und triggert einen Browser-Download
 * über ein temporäres Object-URL. Filename kommt aus dem
 * `Content-Disposition`-Header des Backends.
 */
export async function downloadWorkshopPdf(
  id: string,
  fallbackName: string,
): Promise<void> {
  const response = await api.get<Blob>(`/v1/workshops/${id}/export/pdf`, {
    responseType: "blob",
  });
  const filename =
    parseFilenameFromDisposition(response.headers["content-disposition"]) ??
    `${fallbackName.replace(/[^a-z0-9\-_]+/gi, "_") || "workshop"}.pdf`;
  const url = window.URL.createObjectURL(response.data);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

function parseFilenameFromDisposition(header: unknown): string | null {
  if (typeof header !== "string") return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}
